import React from 'react';
import type { HomeDashboard, HomeStatus } from '../../../api/homeV3Api';
import { actionLabel, formatDate, statusLabels } from '../utils/formatters';
import styles from '../HomeV3.module.css';

interface Props {
    requests: HomeDashboard['recentRequests'];
    totalCount: number;
    status: HomeStatus | null;
    showAll: boolean;
    onStatusChange: (status: HomeStatus | null) => void;
    onToggleAll: () => void;
    onOpen: (itemKey: string) => void;
}

export function RecentRequests({ requests, totalCount, status, showAll, onStatusChange, onToggleAll, onOpen }: Props) {
    const filters: Array<HomeStatus | null> = [null, 'open', 'inProgress', 'completed', 'cancelled'];
    return (
        <section id="recent-requests" className={`${styles.panel} ${styles.requestsPanel}`} tabIndex={-1} aria-labelledby="requests-heading">
            <div className={styles.panelHeader}>
                <div>
                    <p className={styles.eyebrow}>History</p>
                    <h2 id="requests-heading">Recent change requests</h2>
                </div>
                <div className={styles.filterGroup} aria-label="Filter requests by status">
                    {filters.map((filter) => (
                        <button
                            key={filter ?? 'all'}
                            type="button"
                            className={styles.filterButton}
                            aria-pressed={status === filter}
                            onClick={() => onStatusChange(filter)}
                        >
                            {filter ? statusLabels[filter] : 'All'}
                        </button>
                    ))}
                </div>
            </div>
            {requests.length === 0 ? (
                <div className={styles.emptyState}><p className={styles.muted}>No requests match this filter.</p></div>
            ) : (
                <div className={styles.requestList}>
                    {requests.map((request) => (
                            <button key={request.itemKey} type="button" className={styles.requestButton} onClick={() => onOpen(request.itemKey)}>
                                <span className={styles.requestTitle}>
                                    <span>{actionLabel(request.actionId)} · {request.entityName}</span>
                                    <span className={styles.status}>{statusLabels[request.normalizedStatus]}</span>
                                </span>
                                <span className={styles.requestMeta}>{request.requestType} · submitted {formatDate(request.requestedAtUtc)} · updated {formatDate(request.updatedAtUtc)}</span>
                            </button>
                    ))}
                </div>
            )}
            {totalCount > 6 && (
                <button className={styles.secondaryButton} type="button" onClick={onToggleAll}>
                    {showAll ? 'Show fewer requests' : `View all ${totalCount} requests`}
                </button>
            )}
        </section>
    );
}
